/**
 * 姓氏系统
 * 中国古代姓氏制度：姓（血缘）+ 氏（分支/封地/官职）
 * 
 * 历史背景：
 * - 先秦时期，姓代表血缘，氏代表分支
 * - 男子称氏，女子称姓
 * - 如：孔子，子姓孔氏；商鞅，姬姓公孙氏
 */

/**
 * 姓氏类型
 */
export type SurnameType = 
  | 'major' // 大姓 - 姬、姜、子、嬴等上古大姓
  | 'noble' // 贵族姓 - 诸侯国姓
  | 'tribal' // 部落姓 - 四方蛮夷
  | 'common' // 平民姓 - 普通姓氏
  | 'occupational' // 职业姓 - 司马、司徒等官职姓
  | 'regional' // 地域姓 - 以地名为氏

/**
 * 姓氏起源
 */
export type SurnameOrigin =
  | 'legendary' // 传说时代 - 黄帝、炎帝等
  | 'xia' // 夏朝
  | 'shang' // 商朝
  | 'zhou' // 周朝
  | 'spring_autumn' // 春秋
  | 'warring_states' // 战国
  | 'tribal' // 部落

/**
 * 姓氏信息
 */
export interface SurnameInfo {
  id: string
  surname: string // 姓
  type: SurnameType
  origin: SurnameOrigin
  founder?: string // 始祖
  description: string
  regions: string[] // 主要分布地区
  famousClans: string[] // 著名氏族
}

/**
 * 氏族信息
 */
export interface ClanInfo {
  id: string
  surname: string // 所属姓
  clanName: string // 氏名
  founder: string // 创始人
  founderId?: string
  originType: 'fief' | 'official' | 'occupation' | 'place' | 'title' | 'posthumous'
  originDescription: string // 起源说明
  fief?: string // 封地
  territory: string[] // 势力范围
  peakPeriod: string // 鼎盛时期
  description: string
  famousMembers: string[] // 著名成员
}

// ============================================
// 上古大姓
// ============================================

export const ANCIENT_SURNAMES: SurnameInfo[] = [
  {
    id: 'ji',
    surname: '姬',
    type: 'major',
    origin: 'legendary',
    founder: '黄帝',
    description: '黄帝之姓，周朝王族之姓，万姓之祖。周天子及多数诸侯国国君皆姬姓。',
    regions: ['中原', '关中', '河东'],
    famousClans: ['周氏', '鲁氏', '晋氏', '卫氏', '郑氏', '吴氏', '燕氏'],
  },
  {
    id: 'jiang',
    surname: '姜',
    type: 'major',
    origin: 'legendary',
    founder: '炎帝',
    description: '炎帝之姓，神农氏之后。齐国公族之姓，亦为羌族主要姓氏。',
    regions: ['齐地', '羌地', '中原'],
    famousClans: ['吕氏', '申氏', '许氏', '纪氏'],
  },
  {
    id: 'zi',
    surname: '子',
    type: 'major',
    origin: 'shang',
    founder: '契',
    description: '商朝王族之姓，殷商后裔。宋国为子姓，孔子亦子姓。',
    regions: ['商地', '宋地'],
    famousClans: ['殷氏', '宋氏', '孔氏', '墨氏'],
  },
  {
    id: 'ying',
    surname: '嬴',
    type: 'major',
    origin: 'legendary',
    founder: '伯益',
    description: '伯益之后，秦国、赵国公族之姓。东夷部落主要姓氏。',
    regions: ['秦地', '赵地', '东夷'],
    famousClans: ['秦氏', '赵氏', '徐氏', '郯氏'],
  },
  {
    id: 'mi',
    surname: '芈',
    type: 'major',
    origin: 'zhou',
    founder: '鬻熊',
    description: '楚国公族之姓，祝融之后。楚国国君皆芈姓熊氏。',
    regions: ['楚地', '荆蛮'],
    famousClans: ['熊氏', '屈氏', '景氏', '昭氏'],
  },
  {
    id: 'si',
    surname: '姒',
    type: 'major',
    origin: 'xia',
    founder: '大禹',
    description: '夏朝王族之姓，大禹之后。越国为姒姓。',
    regions: ['越地', '夏地'],
    famousClans: ['夏氏', '越氏', '杞氏'],
  },
  {
    id: 'cao',
    surname: '曹',
    type: 'noble',
    origin: 'zhou',
    founder: '叔振铎',
    description: '周文王之子叔振铎之后，曹国公族。',
    regions: ['曹地'],
    famousClans: ['曹氏'],
  },
  {
    id: 'chen',
    surname: '陈',
    type: 'noble',
    origin: 'zhou',
    founder: '妫满',
    description: '舜帝之后，陈国公族。田氏代齐之田氏亦出自陈氏。',
    regions: ['陈地'],
    famousClans: ['陈氏', '田氏'],
  },
]

// ============================================
// 四方部落姓氏
// ============================================

export const TRIBAL_SURNAMES: SurnameInfo[] = [
  {
    id: 'kui',
    surname: '隗',
    type: 'tribal',
    origin: 'legendary',
    description: '鬼方之姓，北方游牧民族。隗姓出自鬼方部落。',
    regions: ['北方草原', '河套'],
    famousClans: ['鬼方氏'],
  },
  {
    id: 'yan',
    surname: '偃',
    type: 'tribal',
    origin: 'legendary',
    description: '东夷之姓，少昊之后。人方、东夷部落主要姓氏。',
    regions: ['东夷', '淮河流域', '山东半岛'],
    famousClans: ['人方氏', '徐氏', '郯氏'],
  },
  {
    id: 'man',
    surname: '蛮',
    type: 'tribal',
    origin: 'tribal',
    description: '南蛮之姓，南方部落。',
    regions: ['南方', '荆蛮', '百越'],
    famousClans: ['荆蛮氏'],
  },
  {
    id: 'rong',
    surname: '戎',
    type: 'tribal',
    origin: 'tribal',
    description: '西戎之姓，西方游牧部落。',
    regions: ['西方', '陇西'],
    famousClans: ['西戎氏'],
  },
]

// ============================================
// 著名氏族
// ============================================

export const FAMOUS_CLANS: ClanInfo[] = [
  // 姬姓氏族
  {
    id: 'zhou_clan',
    surname: '姬',
    clanName: '周',
    founder: '周文王',
    originType: 'title',
    originDescription: '以国为氏，周朝王室',
    territory: ['关中', '洛邑'],
    peakPeriod: '西周、东周',
    description: '周朝王族，天下共主',
    famousMembers: ['周文王', '周武王', '周公旦'],
  },
  {
    id: 'lu_clan',
    surname: '姬',
    clanName: '鲁',
    founder: '周公旦',
    originType: 'fief',
    originDescription: '以封国为氏，周公封于鲁',
    fief: '鲁',
    territory: ['鲁地', '曲阜'],
    peakPeriod: '西周、春秋',
    description: '周公之后，礼仪之邦',
    famousMembers: ['周公旦', '鲁桓公', '孔子'],
  },
  {
    id: 'jin_clan',
    surname: '姬',
    clanName: '晋',
    founder: '唐叔虞',
    originType: 'fief',
    originDescription: '以封国为氏，叔虞封于唐，后改晋',
    fief: '晋',
    territory: ['晋地', '河东'],
    peakPeriod: '春秋',
    description: '春秋霸主，三家分晋',
    famousMembers: ['晋文公', '晋襄公'],
  },
  {
    id: 'qi_wu_clan',
    surname: '姬',
    clanName: '吴',
    founder: '太伯',
    originType: 'fief',
    originDescription: '太伯奔吴，以国为氏',
    fief: '吴',
    territory: ['吴地', '姑苏'],
    peakPeriod: '春秋晚期',
    description: '太伯之后，春秋末期崛起',
    famousMembers: ['吴王寿梦', '吴王阖闾', '吴王夫差'],
  },
  {
    id: 'wei_clan',
    surname: '姬',
    clanName: '卫',
    founder: '康叔',
    originType: 'fief',
    originDescription: '康叔封于卫',
    fief: '卫',
    territory: ['卫地', '朝歌'],
    peakPeriod: '西周、春秋',
    description: '康叔之后，商朝旧地',
    famousMembers: ['卫武公', '卫灵公'],
  },
  {
    id: 'zheng_clan',
    surname: '姬',
    clanName: '郑',
    founder: '郑桓公',
    originType: 'fief',
    originDescription: '宣王封弟友于郑',
    fief: '郑',
    territory: ['郑地', '新郑'],
    peakPeriod: '春秋早期',
    description: '春秋小霸，庄公称霸',
    famousMembers: ['郑桓公', '郑庄公'],
  },
  // 姜姓氏族
  {
    id: 'qi_clan',
    surname: '姜',
    clanName: '吕',
    founder: '太公望',
    originType: 'fief',
    originDescription: '太公望封于齐，以吕为氏',
    fief: '齐',
    territory: ['齐地', '临淄'],
    peakPeriod: '西周、春秋',
    description: '太公之后，五霸之首',
    famousMembers: ['齐太公', '齐桓公', '齐景公'],
  },
  // 子姓氏族
  {
    id: 'song_clan',
    surname: '子',
    clanName: '宋',
    founder: '微子启',
    originType: 'fief',
    originDescription: '周公封微子启于宋，奉商祀',
    fief: '宋',
    territory: ['宋地', '商丘'],
    peakPeriod: '春秋',
    description: '商朝后裔，春秋五霸之一',
    famousMembers: ['宋襄公', '宋景公'],
  },
  {
    id: 'kong_clan',
    surname: '子',
    clanName: '孔',
    founder: '孔父嘉',
    originType: 'posthumous',
    originDescription: '以祖上孔父嘉之字为氏',
    territory: ['鲁地'],
    peakPeriod: '春秋晚期',
    description: '孔子之族，儒家开创者',
    famousMembers: ['孔子', '孔鲤'],
  },
  // 嬴姓氏族
  {
    id: 'qin_clan',
    surname: '嬴',
    clanName: '秦',
    founder: '秦非子',
    originType: 'fief',
    originDescription: '非子封于秦，以国为氏',
    fief: '秦',
    territory: ['秦地', '雍城'],
    peakPeriod: '春秋、战国',
    description: '西陲大夫，春秋五霸之一',
    famousMembers: ['秦襄公', '秦穆公'],
  },
  {
    id: 'zhao_clan',
    surname: '嬴',
    clanName: '赵',
    founder: '造父',
    originType: 'fief',
    originDescription: '造父封于赵城，以赵为氏',
    fief: '赵',
    territory: ['赵地', '晋阳'],
    peakPeriod: '战国',
    description: '战国七雄之一',
    famousMembers: ['赵盾', '赵武灵王'],
  },
  // 芈姓氏族
  {
    id: 'chu_clan',
    surname: '芈',
    clanName: '熊',
    founder: '鬻熊',
    originType: 'title',
    originDescription: '以先祖鬻熊之名为氏',
    territory: ['楚地', '郢都'],
    peakPeriod: '春秋、战国',
    description: '南方大国，楚王皆熊氏',
    famousMembers: ['楚武王', '楚庄王'],
  },
  {
    id: 'qu_clan',
    surname: '芈',
    clanName: '屈',
    founder: '屈瑕',
    originType: 'fief',
    originDescription: '楚武王之子瑕封于屈',
    fief: '屈',
    territory: ['楚地'],
    peakPeriod: '战国',
    description: '楚国三闾大夫，屈原之族',
    famousMembers: ['屈原', '屈完'],
  },
  // 姒姓氏族
  {
    id: 'yue_clan',
    surname: '姒',
    clanName: '越',
    founder: '无余',
    originType: 'fief',
    originDescription: '夏少康封庶子于越',
    fief: '越',
    territory: ['越地', '会稽'],
    peakPeriod: '春秋晚期',
    description: '夏禹之后，卧薪尝胆',
    famousMembers: ['越王勾践', '范蠡'],
  },
]

// ============================================
// 辅助函数
// ============================================

/**
 * 获取姓氏信息
 */
export function getSurnameInfo(surname: string): SurnameInfo | undefined {
  return [...ANCIENT_SURNAMES, ...TRIBAL_SURNAMES].find(s => s.surname === surname)
}

/**
 * 获取氏族信息
 */
export function getClanInfo(clanName: string): ClanInfo | undefined {
  return FAMOUS_CLANS.find(c => c.clanName === clanName)
}

/**
 * 获取某姓的所有氏族
 */
export function getClansBySurname(surname: string): ClanInfo[] {
  return FAMOUS_CLANS.filter(c => c.surname === surname)
}

/**
 * 获取势力的默认姓氏
 */
export function getFactionDefaultSurname(factionId: string): string {
  const factionSurnames: Record<string, string> = {
    'shang': '子',
    'zhou': '姬',
    'qi': '姜',
    'jin': '姬',
    'lu': '姬',
    'song': '子',
    'wei': '姬',
    'zheng': '姬',
    'qin': '嬴',
    'chu': '芈',
    'wu': '姬',
    'yue': '姒',
    'guifang': '隗',
    'qiangfang': '姜',
    'renfang': '偃',
    'dongyi': '偃',
  }
  return factionSurnames[factionId] || '姬'
}

/**
 * 获取势力的默认氏族
 */
export function getFactionDefaultClan(factionId: string): string {
  const factionClans: Record<string, string> = {
    'shang': '殷',
    'zhou': '周',
    'qi': '吕',
    'jin': '晋',
    'lu': '鲁',
    'song': '宋',
    'wei': '卫',
    'zheng': '郑',
    'qin': '秦',
    'chu': '熊',
    'wu': '吴',
    'yue': '越',
    'guifang': '鬼方',
    'qiangfang': '羌',
    'renfang': '人方',
    'dongyi': '东夷',
  }
  return factionClans[factionId] || ''
}

/**
 * 生成完整姓名（姓+氏+名）
 */
export function generateFullName(
  surname: string,
  clanName: string | undefined,
  givenName: string
): string {
  if (clanName && clanName !== surname) {
    return `${surname}${clanName}${givenName}`
  }
  return `${surname}${givenName}`
}

/**
 * 生成姓氏称号（如：姬姓吕氏）
 */
export function generateSurnameTitle(surname: string, clanName?: string): string {
  if (clanName && clanName !== surname) {
    return `${surname}姓${clanName}氏`
  }
  return `${surname}姓`
}
